/**
 * PARAMETERS panel for the V5 Aircraft workbench — live VSP design parms.
 *
 * Renders the `/geometry/aircraft/parms` response as collapsible per-geom
 * sections of number + slider rows. Purely presentational: overrides live in
 * AircraftSurface (which owns the debounced rebuild); this component only
 * reports edits/resets upward. Styles follow the AircraftSurface idiom —
 * inline CSSProperties off the V5 tokens, no hardcoded hexes.
 */

import { useState, type CSSProperties } from "react";

import type { AircraftGeomParms, AircraftParm } from "../../api/aircraft";

/** VSP "no limit" sentinels are around +/-1e12; anything this big is not a real bound. */
const LIMIT_SENTINEL = 1e9;

const isSentinel = (x: number) => !Number.isFinite(x) || Math.abs(x) >= LIMIT_SENTINEL;

/** Sane slider span: VSP's real limits when present; value +/- 50% (min +/-1 so
 *  zero-valued parms still move) when a limit is a sentinel. VSP also ships
 *  huge-but-finite "no limit" bounds (1e6, 1e12, 1e-10) that pass the sentinel
 *  check yet make a useless slider - when the declared span dwarfs the value
 *  scale, fall back to value +/- 50% clamped inside the real limits. Always
 *  widened to include the current value so a typed-in override never strands
 *  the thumb. */
export function sliderRange(
  parm: AircraftParm,
  current: number,
): { lo: number; hi: number; step: number } {
  const half = Math.abs(parm.value) > 1e-9 ? Math.abs(parm.value) * 0.5 : 1;
  let lo = isSentinel(parm.lo) ? parm.value - half : parm.lo;
  let hi = isSentinel(parm.hi) ? parm.value + half : parm.hi;
  if (hi - lo > 200 * Math.max(Math.abs(parm.value), 1)) {
    lo = Math.max(parm.value - half, lo);
    hi = Math.min(parm.value + half, hi);
  }
  if (!(hi > lo)) {
    lo = parm.value - half;
    hi = parm.value + half;
  }
  lo = Math.min(lo, current);
  hi = Math.max(hi, current);
  return { lo, hi, step: (hi - lo) / 200 };
}

function ParmRow({
  parm,
  override,
  disabled,
  onChange,
}: {
  parm: AircraftParm;
  override: number | undefined;
  disabled: boolean;
  onChange: (value: number) => void;
}) {
  const current = override ?? parm.value;
  const { lo, hi, step } = sliderRange(parm, current);
  const edited = override !== undefined && override !== parm.value;
  return (
    <div style={S.row}>
      <div style={S.rowHead}>
        <span style={{ ...S.parmName, ...(edited ? S.parmEdited : null) }} title={parm.id}>
          {parm.name}
        </span>
        <span style={S.groupTag}>{parm.group}</span>
        <input
          type="number"
          style={S.num}
          step="any"
          value={current}
          disabled={disabled}
          onChange={(e) => {
            const v = Number(e.target.value);
            if (Number.isFinite(v)) onChange(v);
          }}
          aria-label={`${parm.name} value`}
        />
      </div>
      <input
        type="range"
        style={S.slider}
        min={lo}
        max={hi}
        step={step}
        value={Math.min(Math.max(current, lo), hi)}
        disabled={disabled}
        onChange={(e) => onChange(Number(e.target.value))}
        aria-label={parm.name}
      />
    </div>
  );
}

export function AircraftParamPanel({
  geoms,
  overrides,
  disabled = false,
  onChange,
  onResetGeom,
  onResetAll,
}: {
  geoms: AircraftGeomParms[];
  /** parm id -> value; parms not present here show their baseline value. */
  overrides: Record<string, number>;
  /** True while a rebuild is in flight — edits stay live, inputs don't. */
  disabled?: boolean;
  onChange: (parmId: string, value: number) => void;
  onResetGeom: (geom: AircraftGeomParms) => void;
  onResetAll: () => void;
}) {
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const overrideCount = Object.keys(overrides).length;

  return (
    <div style={S.root}>
      <div style={S.head}>
        <span style={S.headTitle}>Parameters</span>
        {overrideCount > 0 && (
          <button style={S.reset} onClick={onResetAll} disabled={disabled}>
            Reset all ({overrideCount})
          </button>
        )}
      </div>
      {geoms.map((g) => {
        const isOpen = open[g.id] === true;
        const geomEdits = g.parms.filter((p) => overrides[p.id] !== undefined).length;
        return (
          <div key={g.id}>
            <div
              style={S.geomHead}
              onClick={() => setOpen((o) => ({ ...o, [g.id]: !isOpen }))}
              role="button"
              aria-expanded={isOpen}
            >
              <span style={S.chev}>{isOpen ? "▾" : "▸"}</span>
              <span style={S.geomName}>{g.name}</span>
              <span style={S.geomMeta}>
                {g.type} · {g.parms.length}
                {geomEdits > 0 ? ` · ${geomEdits} edited` : ""}
              </span>
              {geomEdits > 0 && (
                <button
                  style={S.reset}
                  disabled={disabled}
                  onClick={(e) => {
                    e.stopPropagation();
                    onResetGeom(g);
                  }}
                >
                  Reset
                </button>
              )}
            </div>
            {isOpen && (
              <div style={S.geomBody}>
                {g.parms.map((p) => (
                  <ParmRow
                    key={p.id}
                    parm={p}
                    override={overrides[p.id]}
                    disabled={disabled}
                    onChange={(v) => onChange(p.id, v)}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

const S: Record<string, CSSProperties> = {
  root: { display: "flex", flexDirection: "column", borderTop: "1px solid var(--v5-line)" },
  head: { display: "flex", alignItems: "center", gap: 8, padding: "8px 12px",
    fontWeight: 600, fontSize: 12, textTransform: "uppercase", letterSpacing: 0.5,
    opacity: 0.85, borderBottom: "1px solid var(--v5-line)" },
  headTitle: { flex: 1, opacity: 0.8 },
  reset: { background: "var(--v5-surface)", color: "var(--v5-ink)",
    border: "1px solid var(--v5-line)", borderRadius: "var(--v5-radius, 4px)",
    padding: "1px 8px", cursor: "pointer", font: "11px var(--v5-sans, system-ui)" },
  geomHead: { display: "flex", alignItems: "center", gap: 6, padding: "6px 12px",
    cursor: "pointer", borderBottom: "1px solid var(--v5-line)", userSelect: "none" },
  chev: { width: 12, opacity: 0.6, fontSize: 10 },
  geomName: { fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis",
    whiteSpace: "nowrap" },
  geomMeta: { marginLeft: "auto", font: "10px var(--v5-mono, monospace)", opacity: 0.55,
    whiteSpace: "nowrap" },
  geomBody: { display: "flex", flexDirection: "column", gap: 8, padding: "8px 12px",
    borderBottom: "1px solid var(--v5-line)" },
  row: { display: "flex", flexDirection: "column", gap: 2 },
  rowHead: { display: "flex", alignItems: "center", gap: 6, minWidth: 0 },
  parmName: { flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
    fontSize: 12 },
  parmEdited: { color: "var(--v5-primary)", fontWeight: 600 },
  groupTag: { font: "9px var(--v5-mono, monospace)", opacity: 0.5,
    border: "1px solid var(--v5-line)", borderRadius: "var(--v5-radius, 4px)",
    padding: "0 4px", whiteSpace: "nowrap" },
  num: { width: 72, background: "var(--v5-surface)", color: "var(--v5-ink)",
    border: "1px solid var(--v5-line)", borderRadius: "var(--v5-radius, 4px)",
    padding: "1px 4px", font: "11px var(--v5-mono, monospace)" },
  slider: { width: "100%", margin: 0 },
};
